import { supabase } from '../lib/supabase';

/**
 * Messaging Service
 * Handles real-time messaging with off-platform contact detection
 */

/**
 * Get or create a conversation between the current user and another user.
 * If contractId is provided, returns/creates the contract conversation.
 * Otherwise returns/creates the general conversation between the two users.
 */
export async function getOrCreateConversation(otherUserId, contractId = null) {
  try {
    const { data, error } = await supabase
      ?.rpc('get_or_create_conversation', {
        p_other_user_id: otherUserId,
        p_contract_id: contractId || null
      });
    if (error) throw error;
    return { success: true, conversationId: data };
  } catch (error) {
    console.error('Get or create conversation error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Send a message.
 * Always requires receiverId and messageText.
 * If conversationId is not supplied, it will be auto-created via
 * get_or_create_conversation(receiverId, contractId).
 */
export async function sendMessage(messageData) {
  try {
    const { conversationId, receiverId, messageText, contractId } = messageData;
    // Never trust a caller-supplied senderId — always resolve from the active session.
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user?.id) throw new Error('Not authenticated');

    // Resolve conversation — create if needed
    let convId = conversationId;
    if (!convId) {
      const { data: newId, error: convError } = await supabase?.rpc(
        'get_or_create_conversation',
        { p_other_user_id: receiverId, p_contract_id: contractId || null }
      );
      if (convError) throw convError;
      convId = newId;
    }

    const { data, error } = await supabase
      ?.from('messages')
      ?.insert({
        conversation_id: convId,
        sender_id: user.id,
        receiver_id: receiverId,
        message_text: messageText
      })
      ?.select('*')
      ?.single();

    if (error) throw error;

    // Fire-and-forget: notify the receiver of a new unread message
    supabase?.functions?.invoke('send-message-notification', {
      body: { messageId: data?.id, receiverId }
    }).catch((err) => console.warn('send-message-notification failed:', err));

    return { success: true, message: data, conversationId: convId };
  } catch (error) {
    console.error('Send message error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get all messages in a conversation by conversation UUID.
 * Replaces the old getContractMessages / getConversationWithUser split.
 */
export async function getConversationMessages(conversationId) {
  try {
    const { data, error } = await supabase
      ?.from('messages')
      ?.select('*')
      ?.eq('conversation_id', conversationId)
      ?.order('created_at', { ascending: true });

    if (error) throw error;

    return { success: true, messages: data || [] };
  } catch (error) {
    console.error('Get conversation messages error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get messages for a contract conversation.
 * Looks up the contract's conversation, then returns its messages.
 * @param {string} contractId  — UUID of the contract
 * @param {string} otherUserId — UUID of the other participant (needed to find/create convo)
 */
export async function getContractMessages(contractId, otherUserId) {
  try {
    const { data: convId, error: convError } = await supabase?.rpc(
      'get_or_create_conversation',
      { p_other_user_id: otherUserId, p_contract_id: contractId }
    );
    if (convError) throw convError;
    return getConversationMessages(convId);
  } catch (error) {
    console.error('Get contract messages error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * @deprecated Use getConversationMessages(conversationId) instead.
 * Left here only so callers that still import it don't blow up.
 */
export async function getGeneralMessages(userId) {
  console.warn('getGeneralMessages is deprecated — use getConversationMessages(conversationId)');
  return { success: true, messages: [] };
}

/**
 * Get user's conversations (optimized with conversations table)
 */
export async function getUserConversations(userId) {
  try {
    const { data, error } = await supabase
      ?.rpc('get_user_conversations', { p_user_id: userId });

    if (error) throw error;

    return { success: true, conversations: data || [] };
  } catch (error) {
    console.error('Get user conversations error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get messages for the general conversation between two users.
 * Looks up the conversation by participants, then returns messages.
 */
export async function getConversationWithUser(userId1, userId2) {
  try {
    // Look up the general conversation between the two users
    const p1 = userId1 < userId2 ? userId1 : userId2;
    const p2 = userId1 < userId2 ? userId2 : userId1;
    const { data: conv, error: convError } = await supabase
      ?.from('conversations')
      ?.select('id')
      ?.eq('participant1_id', p1)
      ?.eq('participant2_id', p2)
      ?.is('contract_id', null)
      ?.maybeSingle();

    if (convError) throw convError;
    if (!conv) return { success: true, messages: [] };

    return getConversationMessages(conv.id);
  } catch (error) {
    console.error('Get conversation error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Update conversation settings (mute, pin, archive)
 */
export async function updateConversationSettings(conversationId, userId, settings) {
  try {
    // Get current settings
    const { data: conversation, error: fetchError } = await supabase
      ?.from('conversations')
      ?.select('participant_settings')
      ?.eq('id', conversationId)
      ?.single();

    if (fetchError) throw fetchError;

    // Update settings for this user
    const currentSettings = conversation?.participant_settings || {};
    const updatedSettings = {
      ...currentSettings,
      [userId]: {
        ...(currentSettings[userId] || {}),
        ...settings
      }
    };

    const { data, error } = await supabase
      ?.from('conversations')
      ?.update({ participant_settings: updatedSettings })
      ?.eq('id', conversationId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, conversation: data };
  } catch (error) {
    console.error('Update conversation settings error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Mark message as read
 */
export async function markMessageAsRead(messageId) {
  try {
    const { data, error } = await supabase
      ?.from('messages')
      ?.update({ read_at: new Date()?.toISOString() })
      ?.eq('id', messageId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, message: data };
  } catch (error) {
    console.error('Mark message as read error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Subscribe to real-time messages for a specific conversation.
 * Works for both contract and general conversations.
 */
export function subscribeToConversationMessages(conversationId, callback) {
  const subscription = supabase
    ?.channel(`conversation-${conversationId}-messages`)
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        callback(payload?.new);
      }
    )
    ?.subscribe();

  return subscription;
}

/**
 * @deprecated Use subscribeToConversationMessages(conversationId, callback)
 */
export function subscribeToContractMessages(conversationId, callback) {
  return subscribeToConversationMessages(conversationId, callback);
}

/**
 * Subscribe to real-time messages for a user (all conversations).
 * Used to keep the conversations list up-to-date.
 */
export function subscribeToGeneralMessages(userId, callback) {
  const subscription = supabase
    ?.channel(`user-${userId}-messages`)
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
        // Supabase Realtime doesn't support complex filters; we filter client-side
      },
      (payload) => {
        const msg = payload?.new;
        // Notify for any message that involves this user
        if (msg?.sender_id === userId || msg?.receiver_id === userId) {
          callback(msg);
        }
      }
    )
    ?.subscribe();

  return subscription;
}

/**
 * Unsubscribe from messages
 */
export function unsubscribeFromMessages(subscription) {
  if (subscription) {
    supabase?.removeChannel(subscription);
  }
}

/**
 * Get unread message count for user
 */
export async function getUnreadMessageCount(userId) {
  try {
    const { count, error } = await supabase
      ?.from('messages')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('receiver_id', userId)
      ?.is('read_at', null);

    if (error) throw error;

    return { success: true, count: count || 0 };
  } catch (error) {
    console.error('Get unread count error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get unread message count across all conversations for notification badge.
 */
export async function getConversationUnreadCount(userId) {
  return getUnreadMessageCount(userId);
}

/**
 * Mark all unread new_message notifications as read for a user
 * (called when the user opens any conversation thread)
 */
export async function markMessageNotificationsRead(userId, senderId = null) {
  try {
    let query = supabase
      ?.from('user_notifications')
      ?.update({ read: true })
      ?.eq('user_id', userId)
      ?.eq('type', 'new_message')
      ?.eq('read', false);

    // When a specific conversation is opened, only clear that sender's notifications
    // so unread dots for other conversations are preserved.
    if (senderId) {
      query = query?.contains('metadata', { sender_id: senderId });
    }

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Mark message notifications read error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Mark all unread messages in a conversation as read.
 * @param {string} conversationId — UUID of the conversation
 * @param {string} currentUserId  — the user who is reading (don't mark own messages)
 */
export async function markConversationMessagesRead(conversationId, currentUserId) {
  try {
    let query = supabase
      ?.from('messages')
      ?.update({ read_at: new Date().toISOString() })
      ?.eq('conversation_id', conversationId)
      ?.is('read_at', null);

    // Only mark messages sent by the OTHER party as read
    if (currentUserId) {
      query = query?.neq('sender_id', currentUserId);
    }

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Mark conversation read error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get flagged messages (admin)
 */
export async function getFlaggedMessages() {
  try {
    const { data, error } = await supabase
      ?.from('messages')
      ?.select(`
        *,
        conversation:conversations(contract_id)
      `)
      ?.eq('flagged_offplatform', true)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, messages: data || [] };
  } catch (error) {
    console.error('Get flagged messages error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Check if message contains off-platform patterns (client-side validation)
 */
export function detectOffPlatformPatterns(text) {
  const patterns = [];
  let flagged = false;

  // Email pattern
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
  if (emailRegex?.test(text)) {
    patterns?.push('email');
    flagged = true;
  }

  // Phone number patterns
  const phoneRegex = /(\+?1?[-.]?)?\(?[0-9]{3}\)?[-.]?[0-9]{3}[-.]?[0-9]{4}/g;
  if (phoneRegex?.test(text)) {
    patterns?.push('phone');
    flagged = true;
  }

  // Payment services
  const paymentRegex = /\b(paypal|venmo|zelle|cashapp|cash app)\b/gi;
  if (paymentRegex?.test(text)) {
    patterns?.push('payment_service');
    flagged = true;
  }

  // Social media handles
  const socialRegex = /@[a-zA-Z0-9_]+/g;
  if (socialRegex?.test(text)) {
    patterns?.push('social_handle');
    flagged = true;
  }

  return { flagged, patterns };
}